import { Link, useRouterState, useSearch } from "@tanstack/react-router";

import { cn } from "@/lib/utils";

import { usePipelineSetupCount } from "../api";

/**
 * Renewals section sub-nav. Visually matches the underline variant of the
 * design-system <Tabs>, but uses TanStack `<Link>` so it drives URL state
 * (cheaper than wiring Tabs onValueChange to navigate). Anyone designing a
 * non-routing tab strip should reach for `<Tabs variant="underline">` directly.
 *
 * The "Setup" entry is intentionally temporary: it appears only while there
 * are accounts without ``first_term_start_date`` set. Once every account is
 * anchored the badge count drops to zero and the tab disappears.
 */

const STATIC_TABS = [
  { to: "/renewals/dashboard", label: "Dashboard" },
  { to: "/renewals/pipeline", label: "Pipeline" },
  { to: "/renewals/csms", label: "CSMs" },
  { to: "/renewals/data", label: "Data" },
] as const;

export function RenewalsSubNav() {
  const router = useRouterState();
  const current = router.location.pathname;
  // Setup mode is just a deep link to /renewals/pipeline?setup=1 — read the
  // current search so we can highlight the Setup tab vs the Pipeline tab.
  const search = useSearch({ strict: false }) as { setup?: boolean };
  const setupActive = current === "/renewals/pipeline" && !!search?.setup;
  const setupCount = usePipelineSetupCount();
  const pending = setupCount.data?.pending ?? 0;

  return (
    <nav className="flex items-center gap-1 border-b border-border">
      {STATIC_TABS.map((t) => {
        let active = current === t.to || current.startsWith(t.to + "/");
        // The Setup tab shares the /renewals/pipeline route with Pipeline —
        // de-active the plain Pipeline tab whenever ?setup=1 is on.
        if (t.to === "/renewals/pipeline" && setupActive) active = false;
        return (
          <Link
            key={t.to}
            to={t.to}
            search={t.to === "/renewals/pipeline" ? {} : undefined}
            className={cn(
              "-mb-px rounded-t-md border-b-2 border-transparent px-4 py-2 text-sm font-medium transition-colors",
              active
                ? "border-brand text-brand"
                : "text-foreground/70 hover:border-border hover:text-foreground",
            )}
          >
            {t.label}
          </Link>
        );
      })}
      {pending > 0 && (
        <Link
          to="/renewals/pipeline"
          search={{ setup: true }}
          className={cn(
            "-mb-px flex items-center gap-2 rounded-t-md border-b-2 border-transparent px-4 py-2 text-sm font-medium transition-colors",
            setupActive
              ? "border-warning text-warning"
              : "text-foreground/70 hover:border-border hover:text-foreground",
          )}
          title="Accounts that still need a first term start date"
        >
          <span>Setup</span>
          <span
            className={cn(
              "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
              setupActive
                ? "bg-warning text-warning-foreground"
                : "bg-warning-light text-warning",
            )}
          >
            {pending}
          </span>
        </Link>
      )}
    </nav>
  );
}
